import React, {useRef} from 'react';

import RailTooltip from './RailTooltip';
import {useSidebar} from './SidebarContext';
import {NEUTRAL_PLATE_CLASS, PRESS_PLATE_CLASS} from './geometry';
import {cx} from '../../util';

const TOOLTIP_KEY = '__collapse_toggle__';

/**
 * Panel-with-rail glyph, the conventional sidebar-toggle mark. Drawn inline
 * rather than pulled from Heroicons, which has no panel icon in this set.
 */
const PanelIcon = (props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" {...props}>
    <rect x="2.75" y="3.75" width="14.5" height="12.5" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 4.5v11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * Pins the sidebar open or collapses it to a rail.
 *
 * Lives inside the sidebar header rather than the top bar so that it travels
 * with the panel: hovering a collapsed rail reveals this button, which turns a
 * transient peek into a pinned preference in one click. Hidden when the rail
 * is collapsed and unhovered, and when a page has pinned the rail itself
 * (lesson pages), where the preference would have no effect.
 */
const CollapseToggle = ({className}) => {
  const {
    collapsed,
    showLabels,
    forceCollapsed,
    toggleCollapsed,
    hoveredItem,
    requestTooltip,
    clearTooltip,
  } = useSidebar();
  const anchorRef = useRef(null);

  if (forceCollapsed || !showLabels) return null;

  const label = collapsed ? 'Pin sidebar open' : 'Collapse sidebar';

  return (
    <div ref={anchorRef} className={cx('shrink-0', className)}>
      <button
        type="button"
        onClick={toggleCollapsed}
        // Armed by movement rather than entry: expanding the rail on hover
        // mounts this button, and if that happens under a stationary cursor a
        // mouseenter-driven tooltip would pop up unprompted.
        onMouseMove={() => requestTooltip(TOOLTIP_KEY)}
        onMouseLeave={() => clearTooltip(TOOLTIP_KEY)}
        onBlur={() => clearTooltip(TOOLTIP_KEY)}
        aria-label={label}
        aria-pressed={!collapsed}
        className="group relative flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:outline-none"
      >
        <span className={cx(PRESS_PLATE_CLASS, NEUTRAL_PLATE_CLASS)} />
        <PanelIcon className="relative h-[18px] w-[18px]" />
      </button>
      <RailTooltip
        open={hoveredItem === TOOLTIP_KEY}
        anchorRef={anchorRef}
        id="sidebar-toggle-tooltip"
      >
        {label}
      </RailTooltip>
    </div>
  );
};

export default CollapseToggle;
